"use client";

import type { SVGProps } from "react";
import type { VizExportFormat } from "./viz-actions";

export type VizSeriesEntry = {
  key: string;
  label?: string | null;
  color?: string | null;
  metricKey?: string | null;
  groupKey?: string | null;
  groupValue?: string | null;
  strokeDasharray?: string | null;
};

export type VizXAxisTickDensity = "compact" | "balanced" | "sparse";
export type VizAxisPreset = "compact" | "balanced" | "readable";
type VizAxisDirection = "normal" | "reversed";
export type VizAxisDirections = {
  primary?: VizAxisDirection | null;
  secondary?: VizAxisDirection | null;
};
export type VizPaletteMode = "balanced" | "vivid" | "accessible";
export type VizTooltipMode = "compact" | "detailed";
export type VizReferenceLine = {
  value: number;
  label?: string | null;
  color?: string | null;
  axis?: "primary" | "secondary" | null;
};

export type VizReferenceArea = {
  from: number;
  to: number;
  label?: string | null;
  color?: string | null;
  opacity?: number | null;
  axis?: "primary" | "secondary" | null;
};

export type ResolvedVizReferenceLine = {
  key: string;
  value: number;
  label: string | null;
  color: string;
  axis: "primary" | "secondary";
};

export type ResolvedVizReferenceArea = {
  key: string;
  from: number;
  to: number;
  label: string | null;
  color: string;
  opacity: number;
  axis: "primary" | "secondary";
};

export type VizXAxisTickDensityConfig = {
  interval: "preserveStartEnd" | number;
  minTickGap: number;
};

type VizAxisStyle = {
  tickMargin: number;
  tickFontSize: number;
  yAxisWidth: number;
  gridStrokeDasharray: string;
  gridStrokeOpacity: number;
  gridStrokeWidth: number;
};

type VizReferenceLabelPosition = "insideTopLeft" | "insideTopRight";
type VizReferenceLabelProps = {
  value: string;
  position: VizReferenceLabelPosition;
  fill: string;
  fontSize: number;
  dx?: number;
  dy?: number;
};

const BASE_X_AXIS_MIN_TICK_GAP = 12;
const ROTATED_X_AXIS_MIN_TICK_GAP = 0;
const ROTATED_X_AXIS_BASE_HEIGHT = 72;
const ROTATED_X_AXIS_MAX_HEIGHT = 98;
const ROTATED_X_AXIS_BASE_LABEL_LENGTH = 12;
const ROTATED_X_AXIS_EXTRA_LINE_HEIGHT = 14;
const TOOLTIP_DEFAULT_FRACTION_DIGITS = 2;
const TOOLTIP_TINY_FRACTION_DIGITS = 4;
const VIZ_NEUTRAL_GUIDE_STROKE_DASHARRAY = "3 3";
const VIZ_REFERENCE_GUIDE_STROKE_DASHARRAY = "4 4";
const VIZ_GUIDE_STROKE_WIDTH = 1;
const VIZ_REFERENCE_LABEL_FONT_SIZE = 11;

/**
 * Recharts の X 軸ティック配置をまとめた設定。
 *
 * @remarks
 * ai-elements 側のチャートだけで使う値をここへ閉じ込め、共有 chart 実装を
 * `origin/next_release` に戻しても軸レイアウト計算を維持できるようにする。
 */
export type XAxisTickLayout = {
  angle: number;
  textAnchor: "middle" | "end";
  height: number;
  tickDx: number;
  tickMargin: number;
  tickDy: number;
  minTickGap: number;
  bottomMargin: number;
};

type ResolveXAxisTickLayoutArgs = {
  labels: string[];
  rotate: boolean;
  extraLines?: number;
};

/**
 * AI chart 専用の categorical palette。
 *
 * @remarks
 * balanced は blue + warm accent + teal を軸に lightness 差で見せる穏やかな配色、
 * vivid は比較や emphasis を強めたい場面向け、accessible は colorblind-safe な
 * 高識別 palette として役割を分ける。
 */
const SERIES_COLOR_PALETTES: Record<VizPaletteMode, readonly string[]> = {
  balanced: [
    "#2563eb",
    "#f97316",
    "#0f766e",
    "#7c3aed",
    "#d97706",
    "#db2777",
    "#0891b2",
    "#4f46e5",
    "#65a30d",
    "#c2410c",
  ],
  vivid: [
    "#1d4ed8",
    "#ea580c",
    "#0f766e",
    "#9333ea",
    "#e11d48",
    "#0284c7",
    "#ca8a04",
    "#14b8a6",
    "#4f46e5",
    "#be185d",
  ],
  accessible: [
    "#0072B2",
    "#D55E00",
    "#009E73",
    "#CC79A7",
    "#E69F00",
    "#56B4E9",
    "#F0E442",
    "#332288",
    "#117733",
    "#999933",
  ],
};

const AXIS_STYLE_BY_PRESET: Record<VizAxisPreset, VizAxisStyle> = {
  compact: {
    tickMargin: 8,
    tickFontSize: 10,
    yAxisWidth: 60,
    gridStrokeDasharray: "0",
    gridStrokeOpacity: 0.12,
    gridStrokeWidth: 1,
  },
  balanced: {
    tickMargin: 9,
    tickFontSize: 11,
    yAxisWidth: 68,
    gridStrokeDasharray: "0",
    gridStrokeOpacity: 0.14,
    gridStrokeWidth: 1,
  },
  readable: {
    tickMargin: 10,
    tickFontSize: 12,
    yAxisWidth: 76,
    gridStrokeDasharray: "0",
    gridStrokeOpacity: 0.16,
    gridStrokeWidth: 1,
  },
};

export const DEFAULT_EXPORT_FORMATS: VizExportFormat[] = ["png", "csv"];
export const EMPTY_VIZ_ROWS: Array<Record<string, unknown>> = [];
export const EMPTY_VIZ_SERIES: VizSeriesEntry[] = [];
const VIZ_EXPORT_FORMAT_SET = new Set<VizExportFormat>(["png", "csv", "json"]);

const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

const PERCENT_FORMATTER = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function resolveMaxLabelLength(labels: string[]): number {
  let maxLength = 0;
  for (const label of labels) {
    maxLength = Math.max(maxLength, label.length);
  }
  return maxLength;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function removeCommas(value: string): string {
  return value.split(",").join("");
}

export function coerceNumber(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(removeCommas(value));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/**
 * 可視化向けの unknown 値を有限数値へ変換する。
 *
 * @remarks
 * `1,234` のようなカンマ区切り文字列も許可し、解釈できない値は `null` を返す。
 */
export function toFiniteNumberOrNull(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number(removeCommas(value));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function formatValueTick(value: unknown): string {
  const numeric = toFiniteNumberOrNull(value);
  if (numeric === null) return "";
  return COMPACT_NUMBER_FORMATTER.format(numeric);
}

export function formatPercentTick(value: unknown): string {
  const numeric = toFiniteNumberOrNull(value);
  if (numeric === null) return "";
  return PERCENT_FORMATTER.format(numeric);
}

/**
 * renderer 側の軸向き prop を boolean へ正規化する。
 *
 * @remarks
 * rank のような lower-is-better 系は fixture / renderer-ready spec で
 * `reversed` を明示し、chart 実装側ではこの helper を唯一の判定点にする。
 */
export function isVizAxisReversed(
  axisDirections: VizAxisDirections | null | undefined,
  axis: "primary" | "secondary",
): boolean {
  return (axisDirections?.[axis] ?? "normal") === "reversed";
}

function formatTooltipNumber(
  value: number,
  maximumFractionDigits: number,
): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
  }).format(value);
}

function resolveTooltipFractionDigits(value: number): number {
  if (Number.isInteger(value)) {
    return 0;
  }

  const rounded = Number(value.toFixed(TOOLTIP_DEFAULT_FRACTION_DIGITS));
  if (rounded !== 0) {
    return TOOLTIP_DEFAULT_FRACTION_DIGITS;
  }

  return TOOLTIP_TINY_FRACTION_DIGITS;
}

function resolveTooltipPercentFractionDigits(value: number): number {
  const scaledValue = value * 100;
  if (scaledValue === 0) {
    return 0;
  }

  return resolveTooltipFractionDigits(scaledValue);
}

/**
 * AI 可視化 tooltip 用に metric 値を詳細表示へ整形する。
 *
 * @remarks
 * 軸ラベルは compact notation で十分だが、tooltip は値の検査用途なので
 * 桁を落としすぎない formatter を別に持つ。percent は常に 0-1 の比率として扱い、
 * 欠損は `-` として明示する。
 */
export function formatVizTooltipMetricValue(
  value: unknown,
  options: { isPercent?: boolean } = {},
): string {
  const numeric = toFiniteNumberOrNull(value);
  if (numeric === null) {
    return value === null || value === undefined ? "-" : String(value);
  }

  if (options.isPercent) {
    const fractionDigits = resolveTooltipPercentFractionDigits(numeric);
    return `${formatTooltipNumber(numeric * 100, fractionDigits)}%`;
  }

  return formatTooltipNumber(numeric, resolveTooltipFractionDigits(numeric));
}

/**
 * AI 可視化 tooltip の差分値を見やすい signed text へ整形する。
 *
 * @remarks
 * percent metric の absolute diff は percentage point として見せ、
 * percentage diff は通常の percent suffix を使う。
 */
export function formatVizTooltipDiffValue(
  value: number,
  options: {
    diffType?: "absolute" | "percentage";
    isPercent?: boolean;
  } = {},
): string {
  const prefix = value === 0 ? "" : value > 0 ? "+" : "";

  if (options.diffType === "percentage") {
    return `${prefix}${formatTooltipNumber(value, 2)}%`;
  }

  if (options.isPercent) {
    const fractionDigits = resolveTooltipPercentFractionDigits(value);
    return `${prefix}${formatTooltipNumber(value * 100, fractionDigits)}pp`;
  }

  return `${prefix}${formatTooltipNumber(
    value,
    resolveTooltipFractionDigits(value),
  )}`;
}

function normalizeNonBlankColor(
  color: string | null | undefined,
): string | null {
  if (typeof color !== "string") return null;
  const trimmed = color.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveVizAxisPreset(
  preset: VizAxisPreset | null | undefined,
): VizAxisPreset {
  if (preset === "compact" || preset === "readable") {
    return preset;
  }
  return "balanced";
}

export function resolveVizPaletteMode(
  mode: VizPaletteMode | null | undefined,
): VizPaletteMode {
  if (mode === "vivid" || mode === "accessible") {
    return mode;
  }
  return "balanced";
}

export function resolveVizTooltipMode(
  mode: VizTooltipMode | null | undefined,
): VizTooltipMode {
  if (mode === "detailed") {
    return "detailed";
  }
  return "compact";
}

/**
 * 系列色を paletteMode から決定する。
 *
 * @remarks
 * 明示色が指定されている場合はそれを優先し、未指定分のみ palette で補完する。
 */
export function resolveVizSeriesColor(args: {
  index: number;
  color?: string | null;
  paletteMode?: VizPaletteMode | null;
}): string {
  const explicitColor = normalizeNonBlankColor(args.color);
  if (explicitColor) {
    return explicitColor;
  }

  const palette =
    SERIES_COLOR_PALETTES[resolveVizPaletteMode(args.paletteMode)];
  if (palette.length === 0) {
    return "#1f77b4";
  }

  const index = Number.isFinite(args.index) ? Math.max(0, args.index) : 0;
  return palette[index % palette.length] ?? palette[0] ?? "#1f77b4";
}

/**
 * AI 可視化カード向けの軸・グリッド密度を返す。
 *
 * @remarks
 * Recharts の `CartesianGrid` は `stroke` を SVG の presentation attribute として描画する。
 * `hsl(var(--token))` のような CSS token 文字列をそのまま渡すと `stroke: none` へ落ちるため、
 * 色そのものは `currentColor` + className で与え、ここでは dash/opacity/width だけを管理する。
 */
export function resolveVizAxisStyle(args: {
  preset?: VizAxisPreset | null;
  isMobile: boolean;
}): VizAxisStyle {
  const base = AXIS_STYLE_BY_PRESET[resolveVizAxisPreset(args.preset)];
  if (!args.isMobile) {
    return base;
  }

  return {
    tickMargin: Math.max(6, base.tickMargin - 1),
    tickFontSize: Math.max(10, base.tickFontSize - 1),
    yAxisWidth: Math.max(52, base.yAxisWidth - 8),
    gridStrokeDasharray: base.gridStrokeDasharray,
    gridStrokeOpacity: base.gridStrokeOpacity,
    gridStrokeWidth: base.gridStrokeWidth,
  };
}

/**
 * AI 可視化の Cartesian grid props を共通化する。
 *
 * @remarks
 * major grid line は chart 種別ごとに欠けると読み取りルールが揺れるため、
 * Cartesian 系 AI chart は縦横とも常時表示を既定に固定する。
 * dual 軸 chart が `left` / `right` のような明示 axis ID を使う場合は、
 * Recharts 既定の `0` に頼ると grid が軸へ紐づかず線が消えるため、
 * 必要なときだけ呼び出し側から主軸 ID を渡してバインドする。
 */
export function resolveVizCartesianGridProps(args: {
  axisStyle: VizAxisStyle;
  xAxisId?: string | number;
  yAxisId?: string | number;
}) {
  const gridProps = {
    className: "text-muted-foreground",
    stroke: "currentColor",
    strokeOpacity: args.axisStyle.gridStrokeOpacity,
    strokeDasharray: args.axisStyle.gridStrokeDasharray,
    strokeWidth: args.axisStyle.gridStrokeWidth,
    horizontal: true,
    vertical: true,
  } as const;

  return {
    ...gridProps,
    xAxisId: args.xAxisId,
    yAxisId: args.yAxisId,
  } as const;
}

/**
 * AI 可視化チャートの軸線 props を返す。
 *
 * @remarks
 * Recharts の `XAxis` / `YAxis` は `axisLine` object をそのまま SVG `<line>` へ流す。
 * grid より一段濃い本線だけを残したいので、色は CSS `currentColor` へ寄せつつ
 * opacity を個別に制御して example に近いコントラストを作る。
 */
export function resolveVizAxisLineProps(): Pick<
  SVGProps<SVGLineElement>,
  "stroke" | "strokeOpacity" | "strokeWidth" | "style"
> {
  return {
    stroke: "currentColor",
    strokeOpacity: 0.36,
    strokeWidth: 1,
    style: {
      color: "hsl(var(--foreground))",
    },
  };
}

/**
 * AI 可視化チャートの tick line props を返す。
 *
 * @remarks
 * Recharts の example は tick line を消しておらず、軸と接する短い目盛りが
 * あることで読み取りやすさが上がる。AI chart では一度 `false` で消していたため、
 * 軸線より少し軽いが判読できる濃さで共通復帰させる。
 */
export function resolveVizAxisTickLineProps(): Pick<
  SVGProps<SVGLineElement>,
  "stroke" | "strokeOpacity" | "strokeWidth" | "style"
> {
  return {
    stroke: "currentColor",
    strokeOpacity: 0.24,
    strokeWidth: 1,
    style: {
      color: "hsl(var(--foreground))",
    },
  };
}

/**
 * hover 中だけ見せる neutral guide line の見た目を統一する。
 *
 * @remarks
 * crosshair は「読み取るための補助」であり意味を持つ reference line とは役割が違う。
 * `Tooltip.cursor` / `ReferenceLine` どちらでも同じ見た目を使えるように、
 * shared chart container が `border` token へ寄せる sentinel stroke をここへ閉じ込める。
 * 呼び出し側は色指定を知らずに済み、分析対象そのものより前へ出すぎない補助線として扱える。
 */
export function resolveVizNeutralGuideLineProps(): Pick<
  SVGProps<SVGLineElement>,
  | "stroke"
  | "strokeOpacity"
  | "strokeWidth"
  | "strokeDasharray"
  | "strokeLinecap"
> {
  return {
    stroke: "#ccc",
    strokeOpacity: 0.88,
    strokeWidth: VIZ_GUIDE_STROKE_WIDTH,
    strokeDasharray: VIZ_NEUTRAL_GUIDE_STROKE_DASHARRAY,
    strokeLinecap: "round",
  };
}

/**
 * 意味を持つ reference line の stroke を chart 共通の温度感へ揃える。
 *
 * @remarks
 * threshold / target / baseline は色で意味を持つので neutral guide とは分け、
 * ただし dash と line width だけは全 chart で統一して可読性を揃える。
 */
export function resolveVizSemanticReferenceLineProps(args: {
  color: string;
}): Pick<
  SVGProps<SVGLineElement>,
  | "stroke"
  | "strokeOpacity"
  | "strokeWidth"
  | "strokeDasharray"
  | "strokeLinecap"
> {
  return {
    stroke: args.color,
    strokeOpacity: 0.82,
    strokeWidth: VIZ_GUIDE_STROKE_WIDTH,
    strokeDasharray: VIZ_REFERENCE_GUIDE_STROKE_DASHARRAY,
    strokeLinecap: "round",
  };
}

/**
 * reference guide label の typography を chart 横断で揃える。
 *
 * @remarks
 * AI chart では threshold label の見え方が chart ごとに揺れると「別の UI」に見えやすい。
 * 位置の違いだけを呼び出し側で渡し、色・文字サイズの基準はここへ集約する。
 */
export function resolveVizReferenceLabelProps(args: {
  label?: string | null;
  color: string;
  position?: VizReferenceLabelPosition;
  dx?: number;
  dy?: number;
}): VizReferenceLabelProps | undefined {
  if (typeof args.label !== "string" || args.label.trim().length === 0) {
    return undefined;
  }

  return {
    value: args.label,
    position: args.position ?? "insideTopRight",
    fill: args.color,
    fontSize: VIZ_REFERENCE_LABEL_FONT_SIZE,
    ...(typeof args.dx === "number" ? { dx: args.dx } : {}),
    ...(typeof args.dy === "number" ? { dy: args.dy } : {}),
  };
}

/**
 * line 系 chart の active point を共通のリング付き dot で強調する。
 *
 * @remarks
 * activeDot を chart ごとに別設定へすると hover の温度感が揺れるため、
 * 系列色の dot と背景ノックアウト stroke を 1 箇所へ寄せる。
 */
export function resolveVizActiveDotProps(args: {
  radius: number;
  color: string;
}) {
  return {
    r: args.radius,
    fill: args.color,
    stroke: "hsl(var(--background))",
    strokeWidth: 2,
  } as const;
}

export function ensurePngExportFormats(
  formats: VizExportFormat[] | null | undefined,
): VizExportFormat[] {
  const base: VizExportFormat[] =
    Array.isArray(formats) && formats.length > 0
      ? formats
      : DEFAULT_EXPORT_FORMATS;
  const next: VizExportFormat[] = base.includes("png")
    ? [...base]
    : [...base, "png"];

  const unique: VizExportFormat[] = [];
  for (const format of next) {
    if (unique.includes(format)) continue;
    unique.push(format);
  }

  return unique;
}

export function normalizeVizExportFormats(
  formats: string[] | null | undefined,
  defaults: VizExportFormat[],
): VizExportFormat[] {
  if (!Array.isArray(formats) || formats.length === 0) {
    return defaults;
  }

  const normalized: VizExportFormat[] = [];
  for (const format of formats) {
    if (!VIZ_EXPORT_FORMAT_SET.has(format as VizExportFormat)) continue;
    const safeFormat = format as VizExportFormat;
    if (normalized.includes(safeFormat)) continue;
    normalized.push(safeFormat);
  }

  return normalized.length > 0 ? normalized : defaults;
}

function resolveSparseInterval(rowCount: number): "preserveStartEnd" | number {
  if (rowCount > 30) return 3;
  if (rowCount > 18) return 2;
  if (rowCount > 9) return 1;
  return "preserveStartEnd";
}

/**
 * x軸ラベル密度の実効値を解決する。
 * モバイルでは既定を sparse に寄せ、明示指定があればそれを優先する。
 */
export function resolveEffectiveXAxisTickDensity(args: {
  density?: VizXAxisTickDensity | null;
  isMobile: boolean;
}): VizXAxisTickDensity {
  if (args.density) return args.density;
  return args.isMobile ? "sparse" : "balanced";
}

/**
 * チャット内可視化では幅変動イベントが短時間に集中しやすいため、
 * モバイル時は debounce を強めて再計測スパイクを抑える。
 */
export function resolveChartResizeDebounceMs(args: {
  isMobile: boolean;
}): number {
  return args.isMobile ? 360 : 280;
}

/**
 * カテゴリ軸ラベルの回転有無に応じた X 軸ティック配置を決定する。
 *
 * @remarks
 * 長いラベルや比較期間の 2 行表示で必要になる高さ調整を ai-elements 側に残し、
 * 共有 chart のレイアウト責務を増やさないための分離ポイントとして使う。
 */
export function resolveXAxisTickLayout(
  args: ResolveXAxisTickLayoutArgs,
): XAxisTickLayout {
  if (!args.rotate) {
    return {
      angle: 0,
      textAnchor: "middle",
      height: 36,
      tickDx: 0,
      tickMargin: 10,
      tickDy: 0,
      minTickGap: BASE_X_AXIS_MIN_TICK_GAP,
      bottomMargin: 12,
    };
  }

  const maxLabelLength = resolveMaxLabelLength(args.labels);
  const overflowLength = Math.max(
    0,
    maxLabelLength - ROTATED_X_AXIS_BASE_LABEL_LENGTH,
  );
  const extraHeightFromLabel = clamp(Math.ceil(overflowLength / 8) * 4, 0, 12);
  const extraLineCount = Math.max(0, (args.extraLines ?? 1) - 1);
  const extraHeightFromLines =
    extraLineCount * ROTATED_X_AXIS_EXTRA_LINE_HEIGHT;

  const height = clamp(
    ROTATED_X_AXIS_BASE_HEIGHT + extraHeightFromLabel + extraHeightFromLines,
    ROTATED_X_AXIS_BASE_HEIGHT,
    ROTATED_X_AXIS_MAX_HEIGHT,
  );

  return {
    angle: -45,
    textAnchor: "end",
    height,
    tickDx: 0,
    tickMargin: 2,
    tickDy: 2,
    minTickGap: ROTATED_X_AXIS_MIN_TICK_GAP,
    bottomMargin: 14,
  };
}

/**
 * LLM 指定の密度ヒントを Recharts の interval/minTickGap に写像する。
 *
 * - compact: 可能な限り表示（interval=0）
 * - balanced: 既定挙動（preserveStartEnd）
 * - sparse: ラベル間引きを強める
 */
export function resolveXAxisTickDensityConfig(args: {
  density?: VizXAxisTickDensity | null;
  rowCount: number;
  baseMinTickGap: number;
}): VizXAxisTickDensityConfig {
  const density = args.density ?? "balanced";
  if (density === "compact") {
    return {
      interval: 0,
      minTickGap: 0,
    };
  }
  if (density === "sparse") {
    return {
      interval: resolveSparseInterval(args.rowCount),
      minTickGap: Math.max(args.baseMinTickGap, 24),
    };
  }
  return {
    interval: "preserveStartEnd",
    minTickGap: args.baseMinTickGap,
  };
}

export function resolveVizReferenceLines(
  referenceLines: VizReferenceLine[] | null | undefined,
): ResolvedVizReferenceLine[] {
  if (!Array.isArray(referenceLines) || referenceLines.length === 0) {
    return [];
  }

  return referenceLines
    .map((line, index) => {
      if (
        !line ||
        typeof line.value !== "number" ||
        !Number.isFinite(line.value)
      ) {
        return null;
      }
      const label =
        typeof line.label === "string" && line.label.trim().length > 0
          ? line.label.trim()
          : null;
      const color =
        typeof line.color === "string" && line.color.trim().length > 0
          ? line.color.trim()
          : "hsl(var(--muted-foreground))";
      const axis = line.axis === "secondary" ? "secondary" : "primary";
      return {
        key: `${index}-${line.value}`,
        value: line.value,
        label,
        color,
        axis,
      };
    })
    .filter((line): line is ResolvedVizReferenceLine => line !== null);
}

export function resolveVizReferenceAreas(
  referenceAreas: VizReferenceArea[] | null | undefined,
): ResolvedVizReferenceArea[] {
  if (!Array.isArray(referenceAreas) || referenceAreas.length === 0) {
    return [];
  }

  return referenceAreas
    .map((area, index) => {
      if (
        !area ||
        typeof area.from !== "number" ||
        !Number.isFinite(area.from) ||
        typeof area.to !== "number" ||
        !Number.isFinite(area.to)
      ) {
        return null;
      }
      const from = Math.min(area.from, area.to);
      const to = Math.max(area.from, area.to);
      const label =
        typeof area.label === "string" && area.label.trim().length > 0
          ? area.label.trim()
          : null;
      const color =
        typeof area.color === "string" && area.color.trim().length > 0
          ? area.color.trim()
          : "hsl(var(--muted-foreground))";
      const opacity =
        typeof area.opacity === "number" &&
        Number.isFinite(area.opacity) &&
        area.opacity >= 0 &&
        area.opacity <= 1
          ? area.opacity
          : 0.12;
      const axis = area.axis === "secondary" ? "secondary" : "primary";
      return {
        key: `${index}-${from}-${to}`,
        from,
        to,
        label,
        color,
        opacity,
        axis,
      };
    })
    .filter((area): area is ResolvedVizReferenceArea => area !== null);
}

/**
 * 正規化Y軸では ReferenceLine によるドメイン拡張を禁止し、
 * 系列形状の比較を優先する。
 */
export function resolveReferenceLineOverflowMode(args: {
  useNormalizedAxis: boolean;
}): "discard" | "extendDomain" {
  if (args.useNormalizedAxis) return "discard";
  return "extendDomain";
}

/**
 * line chart の reference guide を、dual 軸時に primary/secondary へ割り当てる。
 *
 * @remarks
 * percent target のような secondary guide を left 軸へ固定すると、
 * count 軸のゼロ付近へ落ちて意味を失うため、軸選択を明示的に持たせる。
 */
export function resolveTimeSeriesReferenceLineAxisId(args: {
  useDualAxis: boolean;
  axis?: "primary" | "secondary";
}): "left" | "right" | undefined {
  if (!args.useDualAxis) return undefined;
  if (args.axis === "secondary") return "right";
  return "left";
}

/**
 * area chart の reference guide を、dual 軸時に value/percent へ割り当てる。
 */
export function resolveTimeSeriesAreaReferenceAxisId(args: {
  useDualAxis: boolean;
  axis?: "primary" | "secondary";
}): "value" | "percent" | undefined {
  if (!args.useDualAxis) return undefined;
  if (args.axis === "secondary") return "percent";
  return "value";
}
